/** Shared message helpers for the /workspace/threads inspector and /chat UI. */

import {
	createFactRegisterFold,
	isAcceptedFactRegisterResult,
	type WorkflowFactRegisterFact,
} from "../../../../email-assistant/core/factRegisters";
import {
	isInteractiveTool,
	type ParsedInteraction,
	parseInteraction,
} from "../../../../email-assistant/core/toolInteractions";
import type { ContentPart, SourcePart } from "./contentPart";

export interface ChatMessageLike {
	_id: string;
	/** Convex-document creation time in ms — present on every persisted row. */
	_creationTime?: number;
	key?: string;
	order?: number;
	stepOrder?: number;
	streaming?: boolean;
	status?: string;
	/** Set by agent `onError` on stream failure (abort/OOM/provider); drives outcome variant. */
	error?: string;
	sources?: ReadonlyArray<SourcePart>;
	message?: {
		role: "user" | "assistant" | "tool" | "system";
		content: string | ContentPart[];
	};
}

/** Maps toolCallId to tool result output, to correlate calls with results. */
export function buildToolResultMap(
	messages: Array<{ message?: { role: string; content: unknown } }>,
): Map<string, unknown> {
	const map = new Map<string, unknown>();
	for (const msg of messages) {
		const m = msg.message;
		if (!m) continue;
		if (m.role === "tool" && Array.isArray(m.content)) {
			for (const part of m.content) {
				if (
					part &&
					typeof part === "object" &&
					"type" in part &&
					part.type === "tool-result" &&
					"toolCallId" in part
				) {
					map.set(
						part.toolCallId as string,
						(part as Record<string, unknown>).output ??
							(part as Record<string, unknown>).result,
					);
				}
			}
		}
	}
	return map;
}

/** Unwraps AI SDK JSON tool-result envelopes. */
export function unwrapToolResult(raw: unknown): unknown {
	if (!raw || typeof raw !== "object") return raw;
	if (!("type" in raw) || !("value" in raw) || raw.type !== "json") return raw;
	return raw.value;
}

/** Returns user-facing error text for failed tool results. */
export function toolResultError(raw: unknown): string | null {
	const value = unwrapToolResult(raw);
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	if (
		"type" in value &&
		(value.type === "error-text" || value.type === "error-json")
	) {
		if (
			"value" in value &&
			typeof value.value === "string" &&
			value.value.trim().length > 0
		) {
			return value.value.trim();
		}
		return "Tool reported a failure.";
	}
	if (
		"error" in value &&
		typeof value.error === "string" &&
		value.error.trim().length > 0
	) {
		return value.error.trim();
	}
	const message =
		"message" in value &&
		typeof value.message === "string" &&
		value.message.trim().length > 0
			? value.message.trim()
			: null;
	if (
		("ok" in value && value.ok === false) ||
		("success" in value && value.success === false)
	) {
		return message ?? "Tool reported a failure.";
	}
	// Bare status:"error" may echo an inspected entity; require message text.
	if ("status" in value && value.status === "error") return message;
	return null;
}

function partsOf(
	message: ChatMessageLike["message"],
	sources: ChatMessageLike["sources"] = undefined,
): ContentPart[] {
	const parts: ContentPart[] = !message
		? []
		: typeof message.content === "string"
			? [{ type: "text", text: message.content }]
			: [...message.content];
	for (const source of sources ?? []) {
		if (
			source.sourceType !== "url" ||
			typeof source.url !== "string" ||
			source.url.length === 0
		) {
			continue;
		}
		parts.push(source);
	}
	return parts;
}

function textOf(message: ChatMessageLike["message"]): string {
	if (!message) return "";
	if (typeof message.content === "string") return message.content;
	return message.content
		.filter((p): p is { type: "text"; text: string } => p.type === "text")
		.map((p) => p.text)
		.join("");
}

export interface TurnGroup<M extends ChatMessageLike = ChatMessageLike> {
	/** Stable key (first member's key/_id). */
	key: string;
	role: "user" | "assistant" | "system";
	/** Shared agent `order` for the turn (0 if undefined on inputs). */
	order: number;
	/** Underlying persisted rows in input order. */
	messages: M[];
	/** True if any member is streaming. */
	streaming: boolean;
	/** Concatenated content parts across the group, in step order. */
	parts: ContentPart[];
	/** Text from the LAST assistant message in the group (avoids duplicating intermediate-step text). */
	text: string;
	/** `_creationTime` of the first row — "sent at" for user, "started replying at" for assistant. */
	startedAt?: number;
	/** `_creationTime` of the last row — "reply finished at" once done, else tracks latest step. */
	endedAt?: number;
	/**
	 * True when the last member has `status === "failed"` (agent sets this on
	 * stream error: abort/OOM/provider). Drives the inline outcome card position.
	 */
	failed: boolean;
	/** Raw `onError` string from the failed step; feeds `runOutcomeFromError`. */
	errorMessage?: string;
	/**
	 * True when the last assistant member has `status === "pending"`. Agent
	 * persists an empty pending message the instant a turn starts, before
	 * tool-call parts land — so mid-tool (e.g. ~20s CEAC calls) `streaming`
	 * is false but `pending` is true; use this to detect "waiting on a tool."
	 */
	pending: boolean;
}

/**
 * Fold `useThreadMessages` rows into one group per turn. Agent persists each
 * LLM step of a multi-tool turn as its own row (same `order`, distinct
 * `stepOrder`) — ungrouped, UI would render N cards per turn. Tool rows drop
 * here; they feed `buildToolResultMap` instead.
 */
export function groupMessagesByTurn<M extends ChatMessageLike>(
	messages: ReadonlyArray<M>,
): TurnGroup<M>[] {
	const groups: TurnGroup<M>[] = [];
	for (const m of messages) {
		const role = m.message?.role;
		if (!role) continue;
		const order = m.order ?? 0;
		const last = groups[groups.length - 1];
		if (role === "tool") {
			if (last?.role === "assistant" && last.order === order) {
				last.parts.push(...partsOf(undefined, m.sources));
			}
			continue;
		}
		if (last && last.role === role && last.order === order) {
			last.messages.push(m);
			last.parts.push(...partsOf(m.message, m.sources));
			if (m.streaming) last.streaming = true;
			if (role === "assistant") {
				// Always overwrite — the last assistant message in the group
				// holds the final answer text.
				last.text = textOf(m.message);
			}
			if (typeof m._creationTime === "number") {
				last.endedAt = m._creationTime;
				if (last.startedAt === undefined) last.startedAt = m._creationTime;
			}
			last.failed = m.status === "failed";
			last.errorMessage = m.error ?? last.errorMessage;
			last.pending = m.status === "pending";
		} else {
			groups.push({
				key: m.key ?? m._id,
				role,
				order,
				messages: [m],
				streaming: Boolean(m.streaming),
				parts: partsOf(m.message, m.sources),
				text: textOf(m.message),
				startedAt: m._creationTime,
				endedAt: m._creationTime,
				failed: m.status === "failed",
				errorMessage: m.error,
				pending: m.status === "pending",
			});
		}
	}
	return groups;
}

/** Maps a failed step's raw `onError` string to a RunOutcome variant. */
export function runOutcomeFromError(
	error: string | undefined,
): "cancelled_by_user" | "timed_out" | "failed" | "crashed" {
	if (!error) return "failed";
	const lower = error.toLowerCase();
	if (lower.includes("cancelled by user")) return "cancelled_by_user";
	if (lower.includes("client_detected_stall")) return "crashed";
	// Matches scheduled_t_plus_30_30 and legacy scheduled_t_plus_10_30.
	if (lower.includes("scheduled_t_plus_")) return "timed_out";
	return "failed";
}

export interface PlanPart {
	type: "plan";
	title?: string;
	intro?: string;
	steps: { title: string; body?: string }[];
}

function isPlanPayload(x: unknown): x is {
	title?: string;
	intro?: string;
	steps: { title: string; body?: string }[];
} {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (!Array.isArray(o.steps)) return false;
	if (o.steps.length === 0) return false;
	for (const s of o.steps as unknown[]) {
		if (!s || typeof s !== "object") return false;
		const so = s as Record<string, unknown>;
		if (typeof so.title !== "string" || so.title.length === 0) return false;
		if (so.body !== undefined && typeof so.body !== "string") return false;
	}
	if (o.title !== undefined && typeof o.title !== "string") return false;
	if (o.intro !== undefined && typeof o.intro !== "string") return false;
	return true;
}

/**
 * Pulls `respondWithPlan`'s tool-result (its `execute` is identity, so the
 * result IS the payload) from a turn's parts; null if absent/unvalidated.
 * Last call wins if multiple appear (system prompt says "exactly once").
 */
export function planPartFor(
	parts: ReadonlyArray<ContentPart>,
	toolResults: Map<string, unknown>,
): PlanPart | null {
	let latest: PlanPart | null = null;
	for (const p of parts) {
		if (p.type !== "tool-call") continue;
		const tc = p as { toolCallId: string; toolName: string };
		if (tc.toolName !== "respondWithPlan") continue;
		const payload = unwrapToolResult(toolResults.get(tc.toolCallId));
		if (!isPlanPayload(payload)) continue;
		latest = { type: "plan", ...payload };
	}
	return latest;
}

/** Pulls latest registered interaction from one turn. */
export function interactionPartFor(
	parts: ReadonlyArray<ContentPart>,
): ParsedInteraction | null {
	let latest: ParsedInteraction | null = null;
	for (const p of parts) {
		if (p.type !== "tool-call") continue;
		const tc = p as {
			toolCallId: string;
			toolName: string;
			input?: unknown;
			args?: unknown;
		};
		const interaction = parseInteraction(
			tc.toolName,
			tc.input ?? tc.args,
			tc.toolCallId,
		);
		if (interaction) latest = interaction;
	}
	return latest;
}

export interface InteractionCall {
	toolCallId: string;
	toolName: string;
	input: unknown;
}

// Most recent interactive tool call across messages, or null.
export function latestInteractionCall(
	messages: ReadonlyArray<ChatMessageLike>,
): InteractionCall | null {
	let latest: InteractionCall | null = null;
	for (const msg of messages) {
		for (const p of partsOf(msg.message)) {
			if (p.type !== "tool-call") continue;
			const tc = p as {
				toolCallId: string;
				toolName: string;
				input?: unknown;
				args?: unknown;
			};
			if (!isInteractiveTool(tc.toolName)) continue;
			latest = {
				toolCallId: tc.toolCallId,
				toolName: tc.toolName,
				input: tc.input ?? tc.args,
			};
		}
	}
	return latest;
}

// createClioCalendarEntry — "Meeting scheduled" card

export interface ScheduledMeetingResult {
	eventId: string;
	eventUrl?: string;
	matterTitle?: string;
	summary: string;
	startAt: string;
	endAt: string;
}

function isScheduledMeetingResult(x: unknown): x is ScheduledMeetingResult {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (typeof o.eventId !== "string") return false;
	if (typeof o.summary !== "string") return false;
	if (typeof o.startAt !== "string") return false;
	if (typeof o.endAt !== "string") return false;
	if (o.eventUrl !== undefined && typeof o.eventUrl !== "string") return false;
	if (o.matterTitle !== undefined && typeof o.matterTitle !== "string") {
		return false;
	}
	return true;
}

/** Latest `createClioCalendarEntry` result; same last-wins pattern as `planPartFor`. */
export function scheduledMeetingPartFor(
	parts: ReadonlyArray<ContentPart>,
	toolResults: Map<string, unknown>,
): { toolCallId: string; payload: ScheduledMeetingResult } | null {
	let latest: { toolCallId: string; payload: ScheduledMeetingResult } | null =
		null;
	for (const p of parts) {
		if (p.type !== "tool-call") continue;
		const tc = p as { toolCallId: string; toolName: string };
		if (tc.toolName !== "createClioCalendarEntry") continue;
		const payload = unwrapToolResult(toolResults.get(tc.toolCallId));
		if (!isScheduledMeetingResult(payload)) continue;
		latest = { toolCallId: tc.toolCallId, payload };
	}
	return latest;
}

// eoirCheckCase — "EOIR / ACIS case check" card

export type EoirCheckStatus =
	| "solved"
	| "captcha"
	| "failed"
	| "submitted"
	| "no_result"
	| "error";

export interface EoirCheckResult {
	status: EoirCheckStatus;
	alienNumber: string;
	nationality: string;
	message: string;
	reachedSubmit: boolean;
	sessionId?: string;
	replayUrl?: string;
	screenshotUrl?: string;
	caseDataJson?: string;
	caseSummary?: string;
	solvedBy?: "vision" | "human";
	solveAttempts?: number;
	retryable?: boolean;
	attempt?: number;
	maxAttempts?: number;
}

function isEoirCheckResult(x: unknown): x is EoirCheckResult {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (
		o.status !== "solved" &&
		o.status !== "captcha" &&
		o.status !== "failed" &&
		o.status !== "submitted" &&
		o.status !== "no_result" &&
		o.status !== "error"
	) {
		return false;
	}
	if (typeof o.alienNumber !== "string") return false;
	if (typeof o.nationality !== "string") return false;
	if (typeof o.message !== "string") return false;
	if (typeof o.reachedSubmit !== "boolean") return false;
	for (const k of [
		"sessionId",
		"replayUrl",
		"screenshotUrl",
		"caseDataJson",
		"caseSummary",
	]) {
		if (o[k] !== undefined && typeof o[k] !== "string") return false;
	}
	if (
		o.solvedBy !== undefined &&
		o.solvedBy !== "vision" &&
		o.solvedBy !== "human"
	) {
		return false;
	}
	if (o.solveAttempts !== undefined && typeof o.solveAttempts !== "number") {
		return false;
	}
	if (o.retryable !== undefined && typeof o.retryable !== "boolean") {
		return false;
	}
	for (const k of ["attempt", "maxAttempts"]) {
		if (o[k] !== undefined && typeof o[k] !== "number") return false;
	}
	return true;
}

/** Latest `eoirCheckCase` result; same last-wins pattern as `scheduledMeetingPartFor`. */
export function eoirCheckPartFor(
	parts: ReadonlyArray<ContentPart>,
	toolResults: Map<string, unknown>,
): { toolCallId: string; payload: EoirCheckResult } | null {
	let latest: { toolCallId: string; payload: EoirCheckResult } | null = null;
	for (const p of parts) {
		if (p.type !== "tool-call") continue;
		const tc = p as { toolCallId: string; toolName: string };
		if (tc.toolName !== "eoirCheckCase") continue;
		const payload = unwrapToolResult(toolResults.get(tc.toolCallId));
		if (!isEoirCheckResult(payload)) continue;
		latest = { toolCallId: tc.toolCallId, payload };
	}
	return latest;
}

export const WORKFLOW_TRACKER_RUN_STATUSES = [
	"running",
	"awaiting_review",
	"complete",
	"failed",
	"cancelled",
] as const;

export const WORKFLOW_TRACKER_STEP_STATUSES = [
	"pending",
	"running",
	"awaiting_review",
	"approved",
	"skipped",
	"failed",
] as const;

export interface WorkflowTrackerStep {
	name: string;
	title: string;
	status: (typeof WORKFLOW_TRACKER_STEP_STATUSES)[number];
	note?: string;
}

/** Tracker state synthesized from workflow tool calls. */
export interface WorkflowTrackerState {
	runId?: string;
	journeyId?: string;
	phase?: "requirements" | "autonomous" | "blocked" | "complete";
	workflowKind: string;
	matterFolderName?: string;
	status: (typeof WORKFLOW_TRACKER_RUN_STATUSES)[number];
	currentStep: string | null;
	steps: WorkflowTrackerStep[];
}

function isWorkflowTrackerPayload(x: unknown): x is {
	runId?: string;
	journeyId?: string;
	workflowKind: string;
	matterFolderName?: string;
	status: WorkflowTrackerState["status"];
	currentStep: string | null;
	steps: Array<{
		name: string;
		title: string;
		status: WorkflowTrackerStep["status"];
		note: string | null;
	}>;
} {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (o.runId !== undefined && typeof o.runId !== "string") {
		return false;
	}
	if (o.journeyId !== undefined && typeof o.journeyId !== "string")
		return false;
	if (typeof o.workflowKind !== "string") return false;
	if (
		o.matterFolderName !== undefined &&
		typeof o.matterFolderName !== "string"
	) {
		return false;
	}
	if (!WORKFLOW_TRACKER_RUN_STATUSES.some((s) => s === o.status)) return false;
	if (o.currentStep !== null && typeof o.currentStep !== "string") return false;
	if (!Array.isArray(o.steps) || o.steps.length === 0) return false;
	for (const s of o.steps as unknown[]) {
		if (!s || typeof s !== "object") return false;
		const so = s as Record<string, unknown>;
		if (typeof so.name !== "string" || typeof so.title !== "string")
			return false;
		if (!WORKFLOW_TRACKER_STEP_STATUSES.some((v) => v === so.status)) {
			return false;
		}
		if (so.note != null && typeof so.note !== "string") return false;
	}
	return true;
}

function isWorkflowPlanResult(x: unknown): x is {
	status: "plan";
	runId?: string;
	journeyId?: string;
	phase?: "requirements" | "autonomous";
	steps: Array<{ name: string; title: string }>;
} {
	if (!x || typeof x !== "object") return false;
	const o = x as Record<string, unknown>;
	if (o.status !== "plan") return false;
	if (o.runId !== undefined && typeof o.runId !== "string") {
		return false;
	}
	if (o.journeyId !== undefined && typeof o.journeyId !== "string")
		return false;
	if (
		o.phase !== undefined &&
		o.phase !== "requirements" &&
		o.phase !== "autonomous"
	) {
		return false;
	}
	if (!Array.isArray(o.steps) || o.steps.length === 0) return false;
	for (const s of o.steps as unknown[]) {
		if (!s || typeof s !== "object") return false;
		const so = s as Record<string, unknown>;
		if (typeof so.name !== "string" || typeof so.title !== "string")
			return false;
	}
	return true;
}

/**
 * Synthesizes workflow tracker from transcript: the
 * latest `updateWorkflowTracker` call's args win; before the first
 * update, a successful `startWorkflow` plan seeds step 1 running.
 */
export function workflowStateFor(
	messages: ReadonlyArray<ChatMessageLike>,
	toolResults: Map<string, unknown>,
): WorkflowTrackerState | null {
	let fromPlan: WorkflowTrackerState | null = null;
	let fromTracker: WorkflowTrackerState | null = null;
	for (const msg of messages) {
		for (const p of partsOf(msg.message)) {
			if (p.type !== "tool-call") continue;
			const tc = p as {
				toolCallId: string;
				toolName: string;
				input?: unknown;
				args?: unknown;
			};
			if (
				tc.toolName === "startWorkflow" ||
				tc.toolName === "startPackageWorkflow"
			) {
				const result = unwrapToolResult(toolResults.get(tc.toolCallId));
				if (!isWorkflowPlanResult(result)) continue;
				const input = (tc.input ?? tc.args ?? {}) as Record<string, unknown>;
				fromPlan = {
					...(result.runId ? { runId: result.runId } : {}),
					...(result.journeyId ? { journeyId: result.journeyId } : {}),
					...(result.phase ? { phase: result.phase } : {}),
					workflowKind:
						typeof input.workflowKind === "string" ? input.workflowKind : "",
					...(typeof input.matterFolderName === "string"
						? { matterFolderName: input.matterFolderName }
						: {}),
					status: "running",
					currentStep: result.steps[0]?.name ?? null,
					steps: result.steps.map((step, index) => ({
						name: step.name,
						title: step.title,
						status: index === 0 ? "running" : "pending",
					})),
				};
				fromTracker = null; // A fresh plan supersedes older tracker updates.
			} else if (tc.toolName === "updateWorkflowTracker") {
				const payload = tc.input ?? tc.args;
				if (!isWorkflowTrackerPayload(payload)) continue;
				const trackerResult = unwrapToolResult(
					toolResults.get(tc.toolCallId),
				) as { phase?: unknown } | null;
				const phase =
					trackerResult?.phase === "requirements" ||
					trackerResult?.phase === "autonomous" ||
					trackerResult?.phase === "blocked" ||
					trackerResult?.phase === "complete"
						? trackerResult.phase
						: undefined;
				fromTracker = {
					...(payload.runId
						? { runId: payload.runId }
						: fromPlan?.runId
							? { runId: fromPlan.runId }
							: {}),
					...(payload.journeyId
						? { journeyId: payload.journeyId }
						: fromPlan?.journeyId
							? { journeyId: fromPlan.journeyId }
							: {}),
					...(phase
						? { phase }
						: fromPlan?.phase
							? { phase: fromPlan.phase }
							: {}),
					workflowKind: payload.workflowKind,
					...(payload.matterFolderName
						? { matterFolderName: payload.matterFolderName }
						: {}),
					status: payload.status,
					currentStep: payload.currentStep,
					steps: payload.steps.map((step) => ({
						name: step.name,
						title: step.title,
						status: step.status,
						...(step.note ? { note: step.note } : {}),
					})),
				};
			}
		}
	}
	return fromTracker ?? fromPlan;
}

/** Returns active workflow not cancelled by operator. */
export function activeWorkflowStateFor(
	messages: ReadonlyArray<ChatMessageLike>,
	toolResults: Map<string, unknown>,
	cancelledRunId?: string,
): WorkflowTrackerState | null {
	const state = workflowStateFor(messages, toolResults);
	if (!state) return null;
	if (state.status === "complete" || state.status === "cancelled") return null;
	if (state.runId && state.runId === cancelledRunId) return null;
	return state;
}

/** Fact register synthesized from workflow tool calls. */
export interface WorkflowFactRegisterState {
	/** `_id` of the message carrying this publish call — anchors the card. */
	messageId: string;
	version: number;
	docxWebUrl?: string;
	facts: WorkflowFactRegisterFact[];
}

/** Folds every accepted `publishFactRegister` command in transcript order. */
export function workflowFactRegistersFor(
	messages: ReadonlyArray<ChatMessageLike>,
	toolResults: Map<string, unknown>,
): WorkflowFactRegisterState[] {
	const registers: WorkflowFactRegisterState[] = [];
	const fold = createFactRegisterFold();
	for (const msg of messages) {
		for (const p of partsOf(msg.message)) {
			if (p.type !== "tool-call") continue;
			const tc = p as {
				toolCallId: string;
				toolName: string;
				input?: unknown;
				args?: unknown;
			};
			if (tc.toolName !== "publishFactRegister") continue;
			const result = unwrapToolResult(toolResults.get(tc.toolCallId));
			if (!isAcceptedFactRegisterResult(result)) continue;
			const snapshot = fold.apply(tc.input ?? tc.args);
			// Skipping a command would desync every later snapshot.
			if (!snapshot) return registers;
			registers.push({ messageId: msg._id, ...snapshot });
		}
	}
	return registers;
}
